import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, UserCog, Wand2, MailCheck, CheckCircle2, AlertTriangle, Trash2, Send, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('valoriza_token')}` });

// ── API helpers ────────────────────────────────────────────────────────────
const getGerenteOverrides    = () => axios.get(`${API}/api/admin/gerente-overrides`, { headers: authHeader() }).then(r => r.data);
const getGerenteSuggestions  = () => axios.get(`${API}/api/admin/gerente-suggestions`, { headers: authHeader() }).then(r => r.data);
const upsertGerenteOverride  = (sede_codigo, gerente) => axios.post(`${API}/api/admin/gerente-overrides`, { sede_codigo, gerente }, { headers: authHeader() }).then(r => r.data);
const deleteGerenteOverride  = (code) => axios.delete(`${API}/api/admin/gerente-overrides/${encodeURIComponent(code)}`, { headers: authHeader() });

const getFuzzySuggestions    = (min = 90) => axios.get(`${API}/api/admin/persona-fuzzy-suggestions?min_ratio=${min}`, { headers: authHeader(), timeout: 90000 }).then(r => r.data);
const applyFuzzyCluster      = (canonical, variants) => axios.post(`${API}/api/admin/persona-fuzzy-apply`, { canonical, variants }, { headers: authHeader() }).then(r => r.data);

const sendReminders          = (month, dry_run) => axios.post(`${API}/api/admin/reminders/send?dry_run=${dry_run}${month ? `&month=${month}` : ''}`, null, { headers: authHeader(), timeout: 120000 }).then(r => r.data);
const getReminderHistory     = () => axios.get(`${API}/api/admin/reminders/history`, { headers: authHeader() }).then(r => r.data);
const getNmHuerfanos         = () => axios.get(`${API}/api/admin/nm-huerfanos`, { headers: authHeader() }).then(r => r.data);
const getPersonasMultiples   = () => axios.get(`${API}/api/admin/personas-multiples`, { headers: authHeader() }).then(r => r.data);
const getSedesSinGerente     = () => axios.get(`${API}/api/admin/sedes-sin-gerente-canonico`, { headers: authHeader() }).then(r => r.data);
const getUsuariosSinRls      = () => axios.get(`${API}/api/admin/usuarios-sin-rls`, { headers: authHeader() }).then(r => r.data);

// ── Sub-tab style ──────────────────────────────────────────────────────────
const SUBTAB = (active) => ({
  padding: '5px 14px', borderRadius: 8, fontSize: 12,
  fontWeight: active ? 700 : 500, cursor: 'pointer',
  background: active ? 'rgba(74,158,255,0.18)' : 'transparent',
  border: `1px solid ${active ? 'rgba(74,158,255,0.5)' : 'rgba(74,158,255,0.1)'}`,
  color: active ? '#4A9EFF' : 'var(--vz-text-secondary)',
  transition: 'all .15s',
});

// ═══ GERENTES POR SEDE ═════════════════════════════════════════════════════
function GerentesSection() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data: overrides = [], isLoading: lo } = useQuery({ queryKey: ['gerente-overrides'], queryFn: getGerenteOverrides });
  const { data: suggestions = [], isLoading: ls } = useQuery({ queryKey: ['gerente-suggestions'], queryFn: getGerenteSuggestions });

  const overrideMap = Object.fromEntries(overrides.map(o => [o.sede_codigo, o.gerente]));

  const handleApply = async (sede_codigo, gerente) => {
    try {
      const r = await upsertGerenteOverride(sede_codigo, gerente);
      toast.success(`Gerente fijado para ${sede_codigo}: ${gerente}. ${r.updated_records} registros corregidos.`);
      qc.invalidateQueries({ queryKey: ['gerente-overrides'] });
      qc.invalidateQueries({ queryKey: ['gerente-suggestions'] });
    } catch { toast.error('Error al guardar'); }
  };

  const handleDelete = async (code) => {
    try {
      await deleteGerenteOverride(code);
      toast.success('Override eliminado');
      qc.invalidateQueries({ queryKey: ['gerente-overrides'] });
    } catch { toast.error('Error al eliminar'); }
  };

  const filtered = suggestions.filter(s =>
    !search || s.sede_codigo?.toLowerCase().includes(search.toLowerCase())
           || s.nombre?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="gerentes-section">
      {/* Overrides activos */}
      <div>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--vz-text-secondary)' }}>
          Overrides activos ({overrides.length})
        </p>
        {lo ? <Skeleton className="h-8 w-full" /> : overrides.length === 0 ? (
          <p className="text-xs py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>
            Sin overrides. Aplica uno desde la tabla de sugerencias de abajo.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                {['Sede', 'Centro', 'Gerente canónico', 'Acción'].map(h => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {overrides.map((o, i) => (
                <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <TableCell className="text-xs py-2 font-mono" style={{ color: '#4A9EFF' }}>{o.sede_codigo}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>{o.nombre || '—'}</TableCell>
                  <TableCell className="text-xs py-2 font-medium" style={{ color: '#2E7D32' }}>{o.gerente}</TableCell>
                  <TableCell className="text-xs py-2">
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(o.sede_codigo)} className="h-7 w-7 p-0 hover:bg-red-500/10" title="Eliminar override" data-testid={`delete-gerente-${o.sede_codigo}`}>
                      <Trash2 size={12} style={{ color: '#FF5252' }} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Sugerencias de inconsistencias */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>
            Sedes con gerentes inconsistentes ({suggestions.length})
          </p>
          <div className="relative" style={{ minWidth: 220 }}>
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
            <Input
              placeholder="Buscar sede o centro..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
              style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
              data-testid="gerente-search"
            />
          </div>
        </div>

        {ls ? <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
          : filtered.length === 0 ? (
          <p className="text-xs py-6 text-center" style={{ color: 'var(--vz-text-muted)' }}>
            {suggestions.length === 0 ? 'Sin inconsistencias detectadas.' : 'Sin resultados para la búsqueda.'}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                {['Sede', 'Centro', 'Gerentes en BD (OPS / LID)', 'Recomendado', 'Override actual', 'Acción'].map(h => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.slice(0, 60).map((s, i) => (
                <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <TableCell className="text-xs py-2 font-mono" style={{ color: '#4A9EFF' }}>{s.sede_codigo}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>{s.nombre || '—'}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                    <div className="space-y-0.5">
                      {s.gerentes.map((g, gi) => (
                        <div key={gi} className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium" style={{ color: g.gerente === s.recommended ? '#2E7D32' : 'var(--vz-text-primary)' }}>{g.gerente}</span>
                          <Badge className="text-[9px] px-1 py-0" style={{ background: 'rgba(74,158,255,0.14)', color: '#4A9EFF' }}>OPS {g.ops_n}</Badge>
                          <Badge className="text-[9px] px-1 py-0" style={{ background: 'rgba(46,125,50,0.14)', color: '#2E7D32' }}>LID {g.lid_n}</Badge>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs py-2 font-medium" style={{ color: '#2E7D32' }}>{s.recommended}</TableCell>
                  <TableCell className="text-xs py-2">
                    {s.current_override ? <span style={{ color: '#FF9800' }}>{s.current_override}</span> : <span style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                  </TableCell>
                  <TableCell className="text-xs py-2">
                    <Button size="sm" onClick={() => handleApply(s.sede_codigo, s.recommended)} className="h-7 text-[11px] px-2 gap-1" style={{ background: '#4A9EFF', color: '#fff' }} data-testid={`apply-gerente-${s.sede_codigo}`}>
                      <CheckCircle2 size={11} /> Fijar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {filtered.length > 60 && (
          <p className="text-[11px] text-center py-2" style={{ color: 'var(--vz-text-muted)' }}>
            Mostrando 60 de {filtered.length}. Afinca la búsqueda para ver más.
          </p>
        )}
      </div>
    </div>
  );
}

// ═══ SUGERENCIAS FUZZY DE PERSONAS ═════════════════════════════════════════
function FuzzySection() {
  const qc = useQueryClient();
  const [minRatio, setMinRatio] = useState(90);
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState(null);
  const [applying, setApplying] = useState({});

  const handleScan = async () => {
    setLoading(true);
    try {
      const data = await getFuzzySuggestions(minRatio);
      setClusters(data);
      toast.success(`${data.length} clusters encontrados (umbral ≥${minRatio}%)`);
    } catch { toast.error('Error al generar sugerencias'); }
    finally { setLoading(false); }
  };

  const handleApply = async (idx, canonical, variants) => {
    setApplying(prev => ({ ...prev, [idx]: true }));
    try {
      const r = await applyFuzzyCluster(canonical, variants);
      toast.success(`${canonical} ← ${variants.length} variantes. ${r.updated_records} registros actualizados.`);
      setClusters(prev => prev.filter((_, i) => i !== idx));
      qc.invalidateQueries({ queryKey: ['persona-overrides'] });
    } catch { toast.error('Error al aplicar'); }
    finally { setApplying(prev => { const n = { ...prev }; delete n[idx]; return n; }); }
  };

  const handleApplyAll = async () => {
    if (!clusters?.length) return;
    if (!window.confirm(`¿Aplicar los ${clusters.length} clusters? Esto unificará todas las variantes sugeridas con ratio ≥${minRatio}%.`)) return;
    setLoading(true);
    let total = 0;
    for (const c of clusters) {
      try { const r = await applyFuzzyCluster(c.canonical, c.variants); total += r.updated_records; }
      catch (e) { console.error('apply error', e); }
    }
    toast.success(`Batch completado: ${total} registros actualizados`);
    setClusters([]);
    setLoading(false);
    qc.invalidateQueries({ queryKey: ['persona-overrides'] });
  };

  return (
    <div className="space-y-5" data-testid="fuzzy-section">
      <div className="rounded-xl border p-4" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--vz-text-secondary)' }}>Escanear variantes de nombres</p>
        <p className="text-[11px] mb-3" style={{ color: 'var(--vz-text-muted)' }}>
          Agrupa nombres similares por sede usando fuzzy matching. Revisa las sugerencias antes de aplicarlas.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-[11px]" style={{ color: 'var(--vz-text-secondary)' }}>Umbral similitud:</label>
            <Input type="number" min={70} max={100} value={minRatio} onChange={e => setMinRatio(parseInt(e.target.value) || 90)} className="h-8 w-20 text-xs" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }} />
            <span className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>% (70–100)</span>
          </div>
          <Button size="sm" onClick={handleScan} disabled={loading} className="h-8 text-xs gap-1" style={{ background: '#4A9EFF', color: '#fff' }} data-testid="fuzzy-scan-btn">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
            {loading ? 'Escaneando...' : 'Escanear'}
          </Button>
          {clusters?.length > 0 && (
            <Button size="sm" onClick={handleApplyAll} disabled={loading} variant="outline" className="h-8 text-xs gap-1 border-amber-500 text-amber-500 hover:bg-amber-500/10">
              <AlertTriangle size={12} /> Aplicar TODO ({clusters.length})
            </Button>
          )}
        </div>
      </div>

      {clusters === null ? (
        <p className="text-xs py-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>
          Pulsa "Escanear" para buscar variantes similares en los datos.
        </p>
      ) : clusters.length === 0 ? (
        <p className="text-xs py-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>
          Sin variantes agrupables con umbral ≥{minRatio}%.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {['Sede', 'Nombre canónico', 'Variantes detectadas', 'Registros', 'Acción'].map(h => (
                <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {clusters.slice(0, 100).map((c, i) => (
              <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <TableCell className="text-xs py-2 font-mono" style={{ color: '#4A9EFF' }}>{c.sede_codigo}</TableCell>
                <TableCell className="text-xs py-2 font-medium" style={{ color: '#2E7D32' }}>{c.canonical}</TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                  <div className="space-y-0.5">
                    {c.variants.map((v, vi) => (
                      <div key={vi} className="font-mono text-[11px]" style={{ color: '#FF7043' }}>→ {v}</div>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>{c.count}</TableCell>
                <TableCell className="text-xs py-2">
                  <Button size="sm" onClick={() => handleApply(i, c.canonical, c.variants)} disabled={applying[i]} className="h-7 text-[11px] px-2 gap-1" style={{ background: '#4A9EFF', color: '#fff' }} data-testid={`apply-fuzzy-${i}`}>
                    {applying[i] ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                    Aplicar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {clusters && clusters.length > 100 && (
        <p className="text-[11px] text-center py-2" style={{ color: 'var(--vz-text-muted)' }}>Mostrando 100 de {clusters.length}. Sube el umbral para reducir.</p>
      )}
    </div>
  );
}

// ═══ RECORDATORIOS MENSUALES ═══════════════════════════════════════════════
function RecordatoriosSection() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const { data: history = [], isLoading: lh } = useQuery({ queryKey: ['reminder-history'], queryFn: getReminderHistory });

  const run = async (dry_run) => {
    if (!dry_run && !window.confirm(`¿Enviar recordatorios REALES a los jefes de centro del mes ${month}?`)) return;
    setRunning(true);
    try {
      const r = await sendReminders(month, dry_run);
      setLastResult(r);
      toast.success(dry_run
        ? `Dry-run: ${r.details.length} centros con gap, 0 correos reales`
        : `Enviados: ${r.sent} correos reales`);
      qc.invalidateQueries({ queryKey: ['reminder-history'] });
    } catch { toast.error('Error al procesar'); }
    finally { setRunning(false); }
  };

  return (
    <div className="space-y-5" data-testid="reminders-section">
      <div className="rounded-xl border p-4" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--vz-text-secondary)' }}>Enviar recordatorio mensual</p>
        <p className="text-[11px] mb-3" style={{ color: 'var(--vz-text-muted)' }}>
          Compara OPS y Liderazgo registradas en el mes contra los objetivos del catálogo y envía un email al Jefe de Centro con los faltantes.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 w-36 text-xs" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }} data-testid="reminder-month" />
          <Button size="sm" onClick={() => run(true)} disabled={running} variant="outline" className="h-8 text-xs gap-1" data-testid="reminder-dryrun">
            {running ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Dry-run
          </Button>
          <Button size="sm" onClick={() => run(false)} disabled={running} className="h-8 text-xs gap-1" style={{ background: '#009640', color: '#fff' }} data-testid="reminder-send">
            {running ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Enviar real
          </Button>
        </div>
      </div>

      {/* Último resultado */}
      {lastResult && (
        <div className="space-y-2">
          <p className="text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>
            Resultado {lastResult.month} — {lastResult.dry_run ? 'DRY-RUN' : 'envío real'} · {lastResult.sent} enviados · {lastResult.skipped} sin gap
          </p>
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                {['Sede', 'Centro', 'OPS reg / obj / gap', 'LID reg / obj / gap', 'Jefe', 'Estado'].map(h => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lastResult.details.map((d, i) => (
                <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <TableCell className="text-xs py-2 font-mono" style={{ color: '#4A9EFF' }}>{d.sede}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>{d.nombre || '—'}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                    {d.ops_registradas} / {d.ops_objetivo} / <span style={{ color: d.ops_faltan > 0 ? '#C62828' : '#2E7D32' }}>{d.ops_faltan}</span>
                  </TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                    {d.lid_registradas} / {d.lid_objetivo} / <span style={{ color: d.lid_faltan > 0 ? '#C62828' : '#2E7D32' }}>{d.lid_faltan}</span>
                  </TableCell>
                  <TableCell className="text-[11px] py-2 font-mono" style={{ color: 'var(--vz-text-muted)' }}>{d.jefe_email}</TableCell>
                  <TableCell className="text-xs py-2">
                    {d.sent
                      ? <Badge style={{ background: 'rgba(46,125,50,0.15)', color: '#2E7D32' }}>Enviado</Badge>
                      : <Badge style={{ background: 'rgba(255,152,0,0.15)', color: '#FF9800' }}>{lastResult.dry_run ? 'Dry-run' : 'No enviado'}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Historial */}
      <div>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--vz-text-secondary)' }}>Historial de envíos ({history.length})</p>
        {lh ? <Skeleton className="h-8 w-full" /> : history.length === 0 ? (
          <p className="text-[11px] py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>Sin envíos registrados.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                {['Fecha', 'Mes', 'Modo', 'Enviados', 'Sin gap', 'Centros analizados'].map(h => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.slice(0, 20).map((h, i) => (
                <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <TableCell className="text-[11px] py-2 font-mono" style={{ color: 'var(--vz-text-muted)' }}>{h.run_at?.slice(0, 16).replace('T', ' ')}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>{h.month}</TableCell>
                  <TableCell className="text-xs py-2">
                    {h.dry_run
                      ? <Badge style={{ background: 'rgba(74,158,255,0.15)', color: '#4A9EFF' }}>Dry-run</Badge>
                      : <Badge style={{ background: 'rgba(46,125,50,0.15)', color: '#2E7D32' }}>Real</Badge>}
                  </TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>{h.sent}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-muted)' }}>{h.skipped}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-muted)' }}>{h.details_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ═══ PERSONAS MÚLTIPLES ═══════════════════════════════════════════════════════
function PersonasMultiplesSection() {
  const { data, isLoading } = useQuery({ queryKey: ['personas-multiples'], queryFn: getPersonasMultiples });
  const items = data?.items ?? [];
  const totalTipos = data?.total_tipos ?? 0;
  const totalReg = data?.total_registros ?? 0;

  return (
    <div className="space-y-4" data-testid="personas-multiples-section">
      <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(239,83,80,0.08)', border: '1px solid rgba(239,83,80,0.25)' }}>
        <AlertCircle size={16} style={{ color: '#EF5350', marginTop: 2, flexShrink: 0 }} />
        <div>
          <p className="text-xs font-semibold" style={{ color: '#EF5350' }}>
            {isLoading ? '...' : `${totalReg} registros con múltiples nombres en un campo (${totalTipos} combinaciones)`}
          </p>
          <p className="text-[11px] mt-1" style={{ color: 'var(--vz-text-muted)' }}>
            Estos operarios escribieron varios nombres separados por coma en el campo "Persona" del formulario ArcGIS,
            o dejaron una coma al final del nombre. <strong>No se pueden corregir automáticamente.</strong> Pídeles que
            vuelvan a rellenar o corrijan sus registros en ArcGIS con <strong>un solo nombre por campo</strong>.
          </p>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <p className="text-xs py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>Sin entradas multi-persona. ¡Perfecto!</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Valor en ArcGIS</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Sede</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Negocio</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Gerente</TableHead>
              <TableHead className="text-xs text-right" style={{ color: 'var(--vz-text-muted)' }}>Módulo</TableHead>
              <TableHead className="text-xs text-right" style={{ color: 'var(--vz-text-muted)' }}>Registros</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row, i) => (
              <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <TableCell className="text-xs py-2 max-w-[260px]">
                  <span className="font-mono text-[11px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(239,83,80,0.1)', color: '#EF5350' }}>
                    {row.persona}
                  </span>
                </TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>
                  {row.sede_label || row.sede_codigo || '—'}
                </TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                  {row.negocio || '—'}
                </TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                  {row.gerente || '—'}
                </TableCell>
                <TableCell className="text-xs py-2 text-right">
                  <Badge style={{
                    background: row.modulo === 'OPS' ? 'rgba(74,158,255,0.15)' : 'rgba(0,200,83,0.15)',
                    color: row.modulo === 'OPS' ? '#4A9EFF' : '#00C853',
                  }}>{row.modulo}</Badge>
                </TableCell>
                <TableCell className="text-xs py-2 text-right font-semibold tabular-nums" style={{ color: 'var(--vz-text-primary)' }}>
                  {row.n}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ═══ NM SIN CENTRO ═════════════════════════════════════════════════════════
function NmHuerfanosSection() {
  const { data, isLoading } = useQuery({ queryKey: ['nm-huerfanos'], queryFn: getNmHuerfanos });
  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  return (
    <div className="space-y-4" data-testid="nm-huerfanos-section">
      <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(255,152,0,0.08)', border: '1px solid rgba(255,152,0,0.25)' }}>
        <AlertCircle size={16} style={{ color: '#FF9800', marginTop: 2 }} />
        <div>
          <p className="text-xs font-semibold" style={{ color: '#FF9800' }}>
            {isLoading ? '...' : total} Near Misses sin sede asignada
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Estos registros llegaron de ArcGIS sin código de centro. No son visibles en los filtros por sede
            y no pueden asociarse a gerentes. Pide a los operarios que revisen y reenvíen sus formularios.
          </p>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <p className="text-xs py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>Sin Near Misses huérfanos. ¡Buenas noticias!</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Fecha</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Reportado por</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Negocio</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Clasificación</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Descripción (extracto)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.slice(0, 50).map((nm, i) => (
              <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <TableCell className="text-[11px] py-2 font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                  {nm.fecha_reporte
                    ? (typeof nm.fecha_reporte === 'number'
                        ? new Date(nm.fecha_reporte).toISOString().slice(0, 10)
                        : String(nm.fecha_reporte).slice(0, 10))
                    : '—'}
                </TableCell>
                <TableCell className="text-xs py-2 uppercase" style={{ color: 'var(--vz-text-primary)' }}>
                  {(() => {
                    const v = nm.reportado_persona;
                    if (!v) return '—';
                    return /jordi\s*lahora\s*[_/\\-]\s*iv[áa]n\s*edo/i.test(v) ? 'Jordi Lahora / Iván Edo' : v;
                  })()}
                </TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                  {nm.negocio || nm.sede || '—'}
                </TableCell>
                <TableCell className="text-xs py-2">
                  {nm.clasificacion_nm
                    ? <Badge style={{ background: 'rgba(255,152,0,0.15)', color: '#FF9800' }}>{nm.clasificacion_nm}</Badge>
                    : <Badge style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853' }}>Leve</Badge>}
                </TableCell>
                <TableCell className="text-[11px] py-2 max-w-[220px] truncate" style={{ color: 'var(--vz-text-secondary)' }}>
                  {nm.descripcion?.slice(0, 80) || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ═══ SEDES SIN GERENTE CANÓNICO ════════════════════════════════════════════
function SedesSinGerenteSection() {
  const { data, isLoading } = useQuery({ queryKey: ['sedes-sin-gerente'], queryFn: getSedesSinGerente });
  const items = data?.items ?? [];
  const total = data?.total_huerfanos ?? 0;
  const totalCanonicos = data?.total_canonicos ?? 0;

  return (
    <div className="space-y-4" data-testid="sedes-sin-gerente-section">
      <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.25)' }}>
        <AlertTriangle size={16} style={{ color: '#F44336', marginTop: 2 }} />
        <div>
          <p className="text-xs font-semibold" style={{ color: '#F44336' }}>
            {isLoading ? '...' : total} sedes con datos pero sin entrada en el catálogo oficial de gerencias
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Catálogo oficial: {totalCanonicos} sedes con gerencia asignada (Excel "Listado CT asociados Gerencias").
            Estas sedes <strong>llegaron de ArcGIS pero no aparecen en el Excel</strong> — probablemente son nuevos
            contratos o códigos antiguos. Pídele a Israel/RRHH que las añadan al Excel para que se les asigne gerencia.
          </p>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <p className="text-xs py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>Todas las sedes con datos tienen entrada canónica. ¡Excelente!</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Código</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Sede / Centro</TableHead>
              <TableHead className="text-xs text-right" style={{ color: 'var(--vz-text-muted)' }}>OPS</TableHead>
              <TableHead className="text-xs text-right" style={{ color: 'var(--vz-text-muted)' }}>LID</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Gerente actual (en datos)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((s, i) => (
              <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <TableCell className="text-xs py-2 font-mono" style={{ color: '#F44336', fontWeight: 600 }}>
                  {s.sede_codigo}
                </TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>
                  {s.sede_label || '—'}
                </TableCell>
                <TableCell className="text-xs py-2 text-right font-mono" style={{ color: 'var(--vz-text-secondary)' }}>
                  {s.ops_n}
                </TableCell>
                <TableCell className="text-xs py-2 text-right font-mono" style={{ color: 'var(--vz-text-secondary)' }}>
                  {s.lid_n}
                </TableCell>
                <TableCell className="text-xs py-2">
                  {s.gerentes_actuales?.length
                    ? s.gerentes_actuales.map((g, j) => (
                        <Badge key={j} style={{ background: 'rgba(99,135,255,0.15)', color: '#6387FF', marginRight: 4 }}>{g}</Badge>
                      ))
                    : <span style={{ color: 'var(--vz-text-muted)' }}>(sin asignar)</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ═══ USUARIOS SIN RLS ══════════════════════════════════════════════════════
function UsuariosSinRlsSection() {
  const { data, isLoading } = useQuery({ queryKey: ['usuarios-sin-rls'], queryFn: getUsuariosSinRls });
  const items = data?.items ?? [];
  const total = data?.total_huerfanos ?? 0;
  const totalConRls = data?.total_con_rls ?? 0;

  return (
    <div className="space-y-4" data-testid="usuarios-sin-rls-section">
      <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.25)' }}>
        <AlertTriangle size={16} style={{ color: '#F44336', marginTop: 2 }} />
        <div>
          <p className="text-xs font-semibold" style={{ color: '#F44336' }}>
            {isLoading ? '...' : total} usuarios autenticados sin sedes asignadas
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Total con acceso asignado: {totalConRls}. Estos correos pueden iniciar sesión pero <strong>no ven nada</strong> porque
            no tienen ninguna sede asignada. Detectados desde usuarios, solicitudes de acceso aprobadas y accesos vía Microsoft.
            Asígnales sedes en la pestaña "Accesos" de este panel.
          </p>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <p className="text-xs py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>
          Todos los usuarios autenticados tienen RLS asignada. ¡Bien hecho!
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Email</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Última actividad</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Origen</TableHead>
              <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Sedes solicitadas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((u, i) => (
              <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <TableCell className="text-xs py-2 font-mono" style={{ color: '#F44336', fontWeight: 600 }}>
                  {u.email}
                </TableCell>
                <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>
                  {u.last_seen ? new Date(u.last_seen).toLocaleDateString('es-ES') : '—'}
                </TableCell>
                <TableCell className="text-xs py-2">
                  {(u.sources || []).map((s, j) => (
                    <Badge key={j} style={{ background: 'rgba(99,135,255,0.12)', color: '#6387FF', marginRight: 4, fontSize: '10px' }}>
                      {s.replace('_', ' ')}
                    </Badge>
                  ))}
                </TableCell>
                <TableCell className="text-xs py-2 font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                  {(u.sedes_requested || []).length > 0 ? u.sedes_requested.join(', ') : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ═══ MAIN ══════════════════════════════════════════════════════════════════
export function CalidadTab() {
  const [section, setSection] = useState('gerentes');

  return (
    <div className="space-y-5" data-testid="calidad-tab">
      <div>
        <h3 className="font-montserrat font-bold text-sm text-white mb-0.5">Calidad de datos & notificaciones</h3>
        <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
          Corrige inconsistencias de captura (gerentes, nombres) y envía recordatorios de registro a los jefes de centro.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button style={SUBTAB(section === 'gerentes')} onClick={() => setSection('gerentes')} data-testid="subtab-gerentes">
          <span className="flex items-center gap-1.5"><UserCog size={11} /> Gerentes por sede</span>
        </button>
        <button style={SUBTAB(section === 'fuzzy')} onClick={() => setSection('fuzzy')} data-testid="subtab-fuzzy">
          <span className="flex items-center gap-1.5"><Wand2 size={11} /> Sugerencias fuzzy</span>
        </button>
        <button style={SUBTAB(section === 'nm-huerfanos')} onClick={() => setSection('nm-huerfanos')} data-testid="subtab-nm-huerfanos">
          <span className="flex items-center gap-1.5"><AlertCircle size={11} /> NM sin sede</span>
        </button>
        <button style={SUBTAB(section === 'personas-multiples')} onClick={() => setSection('personas-multiples')} data-testid="subtab-personas-multiples">
          <span className="flex items-center gap-1.5"><AlertTriangle size={11} /> Personas múltiples</span>
        </button>
        <button style={SUBTAB(section === 'sedes-sin-gerente')} onClick={() => setSection('sedes-sin-gerente')} data-testid="subtab-sedes-sin-gerente">
          <span className="flex items-center gap-1.5"><AlertTriangle size={11} /> Sedes sin catálogo</span>
        </button>
        <button style={SUBTAB(section === 'usuarios-sin-rls')} onClick={() => setSection('usuarios-sin-rls')} data-testid="subtab-usuarios-sin-rls">
          <span className="flex items-center gap-1.5"><AlertTriangle size={11} /> Usuarios sin RLS</span>
        </button>
        <button style={SUBTAB(section === 'reminders')} onClick={() => setSection('reminders')} data-testid="subtab-reminders">
          <span className="flex items-center gap-1.5"><MailCheck size={11} /> Recordatorios mensuales</span>
        </button>
      </div>

      {section === 'gerentes' && <GerentesSection />}
      {section === 'fuzzy' && <FuzzySection />}
      {section === 'nm-huerfanos' && <NmHuerfanosSection />}
      {section === 'personas-multiples' && <PersonasMultiplesSection />}
      {section === 'sedes-sin-gerente' && <SedesSinGerenteSection />}
      {section === 'usuarios-sin-rls' && <UsuariosSinRlsSection />}
      {section === 'reminders' && <RecordatoriosSection />}
    </div>
  );
}
